import is from '@sindresorhus/is';
import type { Maybe } from 'true-myth';
import { Result } from 'true-myth';
import type { SetRequired } from 'type-fest';
import { z } from 'zod';

import type {
    JobArchitecture,
    JobArchitectureDebugInformation,
    JobArchitectureFiltersResponse,
    JobArchitectureInflight,
    JobArchitectureMetadata,
    JobArchitectureOnboardingState,
    JobArchitectureOptionsResponse,
    JobArchitecturePath,
    JobArchitectureSearchResponse,
} from '~/shared/data-contract/job-architecture';
import {
    jobArchitectureDebugInfoSchema,
    jobArchitectureFiltersResponseSchema,
    jobArchitectureInflightSchema,
    jobArchitectureOnboardingStateSchema,
    jobArchitectureOptionsResponseSchema,
    jobArchitecturePathSchema,
    jobArchitectureSchema,
    jobArchitectureSearchResponseSchema,
    jobArchitecturesSchema,
    nullableJobArchitectureMetadataSchema,
} from '~/shared/data-contract/job-architecture';
import type { ContributionTimeline } from '~/shared/data-contract/job-architecture-contribution-timeline';
import { contributionTimelineSchema } from '~/shared/data-contract/job-architecture-contribution-timeline';
import type { PaginatedJobArchitectureHistory } from '~/shared/data-contract/job-architecture-history';
import { paginatedJobArchitectureHistorySchema } from '~/shared/data-contract/job-architecture-history';
import type { JobArchitectureSkillLimits } from '~/shared/data-contract/job-architecture-skill-limits';
import { jobArchitectureSkillLimitsSchema } from '~/shared/data-contract/job-architecture-skill-limits';
import type { CareerTrack } from '~/shared/data-contract/job-architecture-trade-union';
import { createCareerTrackWithTradeUnionsSchema } from '~/shared/data-contract/job-architecture-trade-union';
import type { JobCluster } from '~/shared/data-contract/job-cluster';
import { jobClusterWithTypeSchemaTransformed } from '~/shared/data-contract/job-cluster';
import type { FamilyWithClusters, JobFamily } from '~/shared/data-contract/job-family';
import { familyWithClustersSchema, jobFamilyWithTypeSchemaTransformed } from '~/shared/data-contract/job-family';
import type { JobLevel, JobLevelWithReviewer } from '~/shared/data-contract/job-level';
import { jobLevelWithTypeSchemaTransformed } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import { jobLevelDetailsSchema } from '~/shared/data-contract/job-level-details';
import type {
    Competencies,
    JobProfileComponent,
    JobProfileForGenerationWithPrompt,
    JobProfileLegacy,
} from '~/shared/data-contract/job-profile';
import {
    competenciesSchema,
    type JobProfileRegenerationResponse,
    jobProfileLegacySchema,
    jobProfileRegenerationResponseSchema,
} from '~/shared/data-contract/job-profile';
import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';
import { jobProfileVersionsSchema } from '~/shared/data-contract/job-profile-versions';
import type { JobRole, JobRoleWithReviewer } from '~/shared/data-contract/job-role';
import { jobRoleWithTypeSchemaTransformed } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import { jobRoleDetailsSchema } from '~/shared/data-contract/job-role-details';
import type { LevelingFramework, PaginatedLevelingFramework } from '~/shared/data-contract/leveling-framework';
import { levelingFrameworkSchema, paginatedLevelingFrameworkSchema } from '~/shared/data-contract/leveling-framework';
import type {
    CreateLevelingFrameworkPayload,
    UpdateLevelingFrameworkPayload,
} from '~/shared/data-contract/leveling-framework-payloads';
import type { Persons } from '~/shared/data-contract/person';
import { personsSchema } from '~/shared/data-contract/person';
import type {
    PaginatedSkillCatalog,
    SkillCatalogOptions,
    SkillCatalogStatistics,
    SkillShort,
    SkillSyncStatus,
    SkillType,
} from '~/shared/data-contract/skill';
import {
    paginatedSkillCatalogSchema,
    skillCatalogOptionsSchema,
    skillCatalogStatisticsSchema,
    skillShortSchema,
} from '~/shared/data-contract/skill';
import type { SkillDetails } from '~/shared/data-contract/skill-details';
import { skillDetailsSchema } from '~/shared/data-contract/skill-details';
import type { PaginatedUsers } from '~/shared/data-contract/user';
import { userPaginationSchema } from '~/shared/data-contract/user';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError, Pathname } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode, LanguageCodeForJSA } from '~/shared/internationalization';
import type { Translate } from '~/shared/internationalization/translation';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { CompanyInformationGenerationResponse } from '../../data-contract/company-information-generation';
import { companyInformationGenerationResponseSchema } from '../../data-contract/company-information-generation';
import type {
    CustomJobProfileTemplateRequest,
    CustomJobProfileTemplateResponse,
    ManagedJobProfileTemplateResponse,
} from '../../data-contract/job-profile-template';
import {
    customJobProfileTemplateGetResponseSchema,
    customJobProfileTemplateResponseSchema,
    managedJobProfileTemplateResponseArraySchema,
} from '../../data-contract/job-profile-template';
import type { RegenerationCommentsContext } from '../../data-contract/regeneration-comments-context';
import { applyOnboardingDraft } from '../../job-architecture-v2/api/apply-onboarding-draft';
import {
    type JsaRegenerationResult,
    type JsaStructureRegenerationResponse,
    jsaRegenerationResultSchema,
    jsaStructureRegenerationResponseSchema,
    type RegenerateOnboardingJsaDraft,
} from '../../job-architecture-v2/api/real-time/jsa-structure-regeneration-schema';
import {
    type FamiliesRegenerationResult,
    familiesRegenerationResultSchema,
} from '../../job-architecture-v2/api/use-families-regeneration-results';
import type { DraftFamilyRow } from '../../job-architecture-v2/onboarding/table/rows';
import type {
    RedetectSkillsRequestPayload,
    SkillsDetectionResult,
} from '../../legacy/position/api/redetect-skills-request';
import { redetectSkillsWithPromptSchema } from '../../legacy/position/api/redetect-skills-request';

type JsonFetcher = ValidatingHttpFetcherWithBaseUrl;

type SkillWithRequiredId = Partial<SetRequired<SkillShort, 'id'>>;

export type AssignableReviewersItem = {
    readonly id: string;
    readonly architectureId: string;
    readonly type: 'level' | 'role';
};

export type InviteAndAssignRequest = {
    readonly emailAddress: string;
    readonly firstName: string;
    readonly inviteLanguage?: string;
    readonly jobTitle?: string;
    readonly lastName: string;
    readonly roles?: readonly string[];
};

export type AssignReviewerItem = JobLevelDetails | JobLevelWithReviewer | JobRoleDetails | JobRoleWithReviewer;

export type TitleAndDescription = {
    readonly description: string;
    readonly title: string;
};

const downloadLinkResponseSchema = z.object({
    downloadLink: z.string(),
});

export type DownloadLinkResponse = z.infer<typeof downloadLinkResponseSchema>;

const changelogExportResponseSchema = z.object({
    exportId: z.string(),
});

export type ChangelogExportResponse = z.infer<typeof changelogExportResponseSchema>;

export type UserRole = 'HIRING_MANAGER' | 'JOB_ARCHITECT' | 'RECRUITER';

export type GetUsersParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly role?: UserRole;
    readonly sort?: string;
};

export type SkillSourceFilter = 'attributed' | 'manual';

export type GetJobArchitectureSkillsParams = {
    readonly jobArchitectureIds?: readonly string[];
    readonly levelIds?: readonly string[];
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
    readonly roleIds?: readonly string[];
    readonly searchTerm?: string;
    readonly skillTypes?: readonly string[];
    readonly sort?: string;
    readonly sources?: readonly SkillSourceFilter[];
    readonly statusFilter?: readonly string[];
};

export type GetJobArchitectureHistoryParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type GetLevelingFrameworksParams = {
    readonly paged: {
        readonly countOnly?: boolean;
        readonly page: number;
        readonly size: number;
    };
};

export type JobArchitectureFetchers = {
    readonly addCareerTrack: (args: {
        readonly careerTrack: Omit<CareerTrack, 'id' | 'trackType'>;
        readonly jobArchitectureId: string;
    }) => Promise<Result<CareerTrack, HttpFetcherError>>;
    readonly archiveJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly archiveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly assignDefaultLevelingFramework: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly assignReviewer: (args: {
        readonly item: AssignReviewerItem;
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly bulkApplyOnboardingDraft: (args: {
        readonly draft: readonly DraftFamilyRow[];
        readonly jobArchitectureId: string;
        readonly languageCode: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly copyJobClusters: (args: {
        readonly jobClusters: NonEmptyArray<JobCluster>;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobLevels: (args: {
        readonly jobLevels: NonEmptyArray<JobLevel>;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly copyJobRoles: (args: {
        readonly jobClusterId: string;
        readonly jobRoles: NonEmptyArray<JobRole>;
    }) => Promise<Result<unknown, Error>>;
    readonly createJobArchitecture: () => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly createJobArchitectureLevelingFramework: (args: {
        readonly jobArchitectureId: string;
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly createLevelingFramework: (args: {
        readonly payload: CreateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly createSkillCatalogEntry: (args: {
        readonly skillId: string;
        readonly syncStatus: SkillSyncStatus;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobArchitecture: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteJobClusters: (jobClusters: NonEmptyArray<JobCluster>) => Promise<Result<unknown, Error>>;
    readonly deleteJobFamilies: (jobFamilies: NonEmptyArray<JobFamily>) => Promise<Result<unknown, Error>>;
    readonly deleteJobLevels: (
        jobLevels: NonEmptyArray<Pick<JobLevel, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteJobRoles: (
        jobRoles: NonEmptyArray<Pick<JobRole, 'architectureId' | 'id'>>,
    ) => Promise<Result<unknown, Error>>;
    readonly deleteLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteSkillCatalogEntry: (args: {
        readonly skillId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly editJobLevelVersionNote: (args: {
        readonly comment: string | null;
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly editJobRoleVersionNote: (args: {
        readonly comment: string | null;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly exportJobArchitecture: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly requestChangelogExport: (args: {
        readonly jobArchitectureId: string;
        readonly dateFrom?: string;
        readonly dateTo?: string;
        readonly familyOrClusterId?: string;
        readonly recipientEmail?: string;
    }) => Promise<Result<ChangelogExportResponse, HttpFetcherError>>;
    readonly exportJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly languageCode: string;
        readonly version?: number;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly exportJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly languageCode: string;
        readonly version?: number;
    }) => Promise<Result<DownloadLinkResponse, HttpFetcherError>>;
    readonly fetchArchivedJobLevels: (args: {
        readonly includeProfiles?: boolean;
        readonly jobArchitectureId: string;
    }) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchArchivedJobRoles: (args: {
        readonly includeProfiles?: boolean;
        readonly jobArchitectureId: string;
    }) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchAssignableReviewers: (args: {
        readonly abortSignal?: AbortSignal;
        readonly jobRoleOrJobLevel: AssignableReviewersItem;
        readonly searchTerm?: string;
    }) => Promise<Result<Persons, HttpFetcherError>>;
    readonly fetchCareerTracks: (
        jobArchitectureId: string,
    ) => Promise<Result<readonly CareerTrack[], HttpFetcherError>>;
    readonly fetchCompanyInformationGenerationStatus: (args: {
        readonly jobArchitectureId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<CompanyInformationGenerationResponse, HttpFetcherError>>;
    readonly fetchCompetencies: () => Promise<Result<Competencies, HttpFetcherError>>;
    readonly fetchContributionTimeline: (args: {
        readonly days?: number;
        readonly jobArchitectureId: string;
        readonly timezone: string;
    }) => Promise<Result<ContributionTimeline, HttpFetcherError>>;
    readonly fetchDebugInformation: (
        item: JobCluster | JobFamily | JobLevel | JobRole,
    ) => Promise<Result<JobArchitectureDebugInformation, HttpFetcherError>>;
    readonly fetchFamiliesWithClusters: (
        architectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly FamilyWithClusters[], HttpFetcherError>>;
    readonly fetchJobArchitecture: (
        jobArchitectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly fetchJobArchitectureFilters: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitectureFiltersResponse, HttpFetcherError>>;
    readonly fetchJobArchitectureHistory: (args: {
        readonly jobArchitectureId: string;
        readonly params: GetJobArchitectureHistoryParams;
    }) => Promise<Result<PaginatedJobArchitectureHistory, HttpFetcherError>>;
    readonly fetchJobArchitectureInflight: (
        jobArchitectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<JobArchitectureInflight, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureLevelingFrameworks: (args: {
        readonly jobArchitectureId: string;
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchJobArchitectureMetadata: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureMetadata | null, HttpFetcherError>>;
    readonly fetchJobArchitectureOnboardingState: (
        jobArchitectureId: string,
    ) => Promise<Result<JobArchitectureOnboardingState, HttpFetcherError>>;
    readonly fetchJobArchitectureOptions: () => Promise<Result<JobArchitectureOptionsResponse, HttpFetcherError>>;
    readonly fetchJobArchitectures: () => Promise<Result<readonly JobArchitecture[], HttpFetcherError>>;
    readonly fetchJobArchitectureSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly params: GetJobArchitectureSkillsParams;
    }) => Promise<Result<PaginatedSkillCatalog, HttpFetcherError>>;
    readonly fetchJobClusters: (
        architectureId: string,
        familyId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobCluster[], HttpFetcherError>>;
    readonly fetchJobFamilies: (
        architectureId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobFamily[], HttpFetcherError>>;
    readonly fetchJobLevels: (
        architectureId: string,
        roleId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobLevel[], HttpFetcherError>>;
    readonly fetchJobLevelVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchJobRoles: (
        architectureId: string,
        clusterId: string,
        options?: { readonly signal?: AbortSignal },
    ) => Promise<Result<readonly JobRole[], HttpFetcherError>>;
    readonly fetchJobRoleVersions: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<JobProfileVersions, HttpFetcherError>>;
    readonly fetchLevelingFramework: (args: {
        readonly frameworkId: string;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly fetchLevelingFrameworks: (args: {
        readonly params: GetLevelingFrameworksParams;
    }) => Promise<Result<PaginatedLevelingFramework, HttpFetcherError>>;
    readonly fetchRelatedSkills: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly skills: readonly SkillWithRequiredId[];
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly fetchSkillDetails: (args: {
        readonly language: string;
        readonly skillId: string;
    }) => Promise<Result<SkillDetails, HttpFetcherError>>;
    readonly fetchSkillLimits: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitectureSkillLimits, HttpFetcherError>>;
    readonly fetchSkillCatalogOptions: () => Promise<Result<SkillCatalogOptions, HttpFetcherError>>;
    readonly fetchSkillCatalogStatistics: (args: {
        readonly jobArchitectureIds?: readonly string[];
        readonly levelIds?: readonly string[];
        readonly roleIds?: readonly string[];
        readonly searchTerm?: string;
        readonly skillTypes?: readonly SkillType[];
        readonly sources?: readonly SkillSourceFilter[];
        readonly statusFilter?: readonly SkillSyncStatus[];
    }) => Promise<Result<SkillCatalogStatistics, HttpFetcherError>>;
    readonly fetchSkillLocales: () => Promise<Result<readonly string[], HttpFetcherError>>;
    readonly fetchSkillsBySearchTerm: (args: {
        readonly acceptLanguage: LanguageCode;
        readonly searchTerm: string;
    }) => Promise<Result<readonly SkillShort[], HttpFetcherError>>;
    readonly generateJobArchitectureWithAI: (args: {
        readonly awaitKnowledgeBaseIngestion?: boolean;
        readonly jobArchitectureId: string;
        readonly type: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobClustersWithAI: (args: {
        readonly jobArchitectureId: string;
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobFamilyWithAI: (jobArchitectureId: string) => Promise<Result<unknown, Error>>;
    readonly generateJobLevelsWithAI: (args: {
        readonly autoSelectTracks: boolean;
        readonly careerTracks: readonly string[];
        readonly createRoleCopy: boolean;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly respectSkillsFromRole: boolean;
    }) => Promise<Result<unknown, Error>>;
    readonly generateJobRolesWithAI: (args: {
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly getJobArchitectureItemPath: (args: {
        readonly itemId: string;
        readonly jobArchitectureId: string;
    }) => Promise<Result<JobArchitecturePath, HttpFetcherError>>;
    readonly getUsers: (args: GetUsersParams) => Promise<Result<PaginatedUsers, HttpFetcherError>>;
    readonly inviteAndAssignToLevel: (args: {
        readonly jobArchitectureId: string;
        readonly levelId: string;
        readonly request: InviteAndAssignRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly inviteAndAssignToRole: (args: {
        readonly jobArchitectureId: string;
        readonly request: InviteAndAssignRequest;
        readonly roleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly moveJobClusters: (args: {
        readonly jobClusters: readonly JobCluster[];
        readonly jobFamilyId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevels: readonly JobLevel[];
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, Error>>;
    readonly moveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobClusterId: string;
        readonly jobRoles: readonly JobRole[];
    }) => Promise<Result<unknown, Error>>;
    readonly patchJobArchitecture: (args: {
        readonly jobArchitecture: JobArchitecture;
        readonly languageCode: string;
    }) => Promise<Result<JobArchitecture, HttpFetcherError>>;
    readonly patchJobArchitectureMetadata: (args: {
        readonly jobArchitectureId: string;
        readonly metadata: Partial<JobArchitectureMetadata>;
        readonly allowJsaRename?: boolean;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly patchJobArchitectureOnboardingState: (args: {
        readonly jobArchitectureId: string;
        readonly onboardingState: Partial<JobArchitectureOnboardingState>;
    }) => Promise<Result<JobArchitectureOnboardingState, HttpFetcherError>>;
    readonly patchJobCluster: (args: {
        readonly jobCluster: JobCluster;
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly patchJobFamily: (args: {
        readonly jobFamily: JobFamily;
        readonly languageCode: string;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly patchJobLevel: (args: {
        readonly jobLevel: JobLevel;
        readonly languageCode: string;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly patchJobRole: (args: {
        readonly jobRole: JobRole;
        readonly languageCode: string;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly persistJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly persistJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly persistJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly persistJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly languageCode: string;
        readonly payload: TitleAndDescription & { readonly parentId: string };
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly publishJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly publishJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly putJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly jobCluster: JobCluster;
        readonly jobFamilyId: string;
    }) => Promise<Result<JobCluster, HttpFetcherError>>;
    readonly putJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly jobFamily: JobFamily;
    }) => Promise<Result<JobFamily, HttpFetcherError>>;
    readonly readJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<JobLevel, HttpFetcherError>>;
    readonly readJobLevelDetails: (args: {
        readonly includeComments?: boolean;
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly signal?: AbortSignal;
        readonly version: string | undefined;
    }) => Promise<Result<JobLevelDetails, HttpFetcherError>>;
    readonly readJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly signal?: AbortSignal;
    }) => Promise<Result<JobRole, HttpFetcherError>>;
    readonly readJobRoleDetails: (args: {
        readonly includeComments?: boolean;
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly signal?: AbortSignal;
        readonly version: string | undefined;
    }) => Promise<Result<JobRoleDetails, HttpFetcherError>>;
    readonly redetectSkills: (
        args: RedetectSkillsRequestPayload,
    ) => Promise<Result<SkillsDetectionResult, HttpFetcherError>>;
    readonly regenerateJobProfileDescription: (args: {
        readonly acceptLanguage: LanguageCodeForJSA;
        readonly additionalPrompt: Maybe<string>;
        readonly commentsContext?: RegenerationCommentsContext;
        readonly jobProfile: JobProfileComponent & JobProfileForGenerationWithPrompt;
    }) => Promise<Result<JobProfileRegenerationResponse, HttpFetcherError>>;
    readonly deleteFamiliesRegenerationResults: (args: {
        readonly jobArchitectureId: string;
        readonly regenerationId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly fetchFamiliesRegenerationResults: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<FamiliesRegenerationResult, HttpFetcherError>>;
    readonly fetchRegenerationResult: (args: {
        readonly jobArchitectureId: string;
        readonly regenerationId: string;
    }) => Promise<Result<JsaRegenerationResult, HttpFetcherError>>;
    readonly regenerateOnboarding: (args: {
        readonly jobArchitectureId: string;
        readonly jsaDraft: RegenerateOnboardingJsaDraft;
        readonly prompt: string;
    }) => Promise<Result<JsaStructureRegenerationResponse, HttpFetcherError>>;
    readonly reviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly reviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly submitCompanyInformationGeneration: (args: {
        readonly jobArchitectureId: string;
        readonly payload: {
            readonly companyDomain: string | null;
            readonly companyName: string;
        };
        readonly regenerate: boolean;
        readonly signal?: AbortSignal;
    }) => Promise<Result<CompanyInformationGenerationResponse, HttpFetcherError>>;
    readonly searchJobArchitecture: (args: {
        readonly jobArchitectureId: string;
        readonly page?: number;
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly size?: number;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }) => Promise<Result<JobArchitectureSearchResponse, HttpFetcherError>>;
    readonly searchJobArchitectures: (args: {
        readonly page?: number;
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly size?: number;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }) => Promise<Result<JobArchitectureSearchResponse, HttpFetcherError>>;
    readonly unarchiveJobLevel: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobLevels: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobRole: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unarchiveJobRoles: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleIds: NonEmptyArray<string>;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unassignReviewer: (args: {
        readonly item: {
            readonly id: string;
            readonly architectureId: string;
            readonly type: 'job-level' | 'job-role';
        };
    }) => Promise<Result<JobLevel | JobRole, HttpFetcherError>>;
    readonly unreviewJobLevelVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobLevelId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly unreviewJobRoleVersion: (args: {
        readonly jobArchitectureId: string;
        readonly jobRoleId: string;
        readonly version: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly updateJobArchitectureLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly jobArchitectureId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly updateJobProfile: (args: {
        readonly acceptLanguage: string;
        readonly jobProfile: JobProfileLegacy;
        readonly versionComment?: string;
        readonly withAutoTranslation: boolean;
    }) => Promise<Result<JobProfileLegacy, HttpFetcherError>>;
    readonly updateLevelingFramework: (args: {
        readonly frameworkId: string;
        readonly payload: UpdateLevelingFrameworkPayload;
    }) => Promise<Result<LevelingFramework, HttpFetcherError>>;
    readonly updateSkillCatalogEntry: (args: {
        readonly skillId: string;
        readonly syncStatus: SkillSyncStatus;
    }) => Promise<Result<unknown, HttpFetcherError>>;

    // Job Profile Template fetchers
    readonly fetchManagedJobProfileTemplates: () => Promise<
        Result<readonly ManagedJobProfileTemplateResponse[], HttpFetcherError>
    >;
    readonly fetchCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
    }) => Promise<Result<CustomJobProfileTemplateResponse | null, HttpFetcherError>>;
    readonly putCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
        readonly templateId: string;
        readonly payload: CustomJobProfileTemplateRequest;
    }) => Promise<Result<CustomJobProfileTemplateResponse, HttpFetcherError>>;
    readonly deleteCustomJobProfileTemplate: (args: {
        readonly jobArchitectureId: string;
        readonly templateId: string;
    }) => Promise<Result<unknown, HttpFetcherError>>;
    readonly postJobProfileTemplatePreview: (args: {
        readonly payload: CustomJobProfileTemplateRequest;
    }) => Promise<Result<unknown, HttpFetcherError>>;
};

export type JobArchitectureFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: JsonFetcher;
};

export const createJobArchitectureFetchers = (
    dependencies: JobArchitectureFetchersDependencies,
): JobArchitectureFetchers => {
    const { cobrainerApiFetcher, translate, validatingCobrainerApiFetcher } = dependencies;

    const generateWithAiRequestTimeout = 60_000;

    const careerTrackWithTradeUnionsSchema = createCareerTrackWithTradeUnionsSchema(translate);
    const addCareerTrack: JobArchitectureFetchers['addCareerTrack'] = async ({ careerTrack, jobArchitectureId }) => {
        return validatingCobrainerApiFetcher.postJson(careerTrackWithTradeUnionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
            payload: careerTrack,
        });
    };

    const assignReviewer: JobArchitectureFetchers['assignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: item.reviewer,
        };

        if ('type' in item && item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const unassignReviewer: JobArchitectureFetchers['unassignReviewer'] = async ({ item }) => {
        const payload = {
            reviewer: null,
        };

        if (item.type === 'job-role') {
            return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
                pathname: `/job-architectures/${item.architectureId}/roles/${item.id}`,
                payload,
            });
        }

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${item.architectureId}/levels/${item.id}`,
            payload,
        });
    };

    const copyJobClusters: JobArchitectureFetchers['copyJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/copy-to/${jobFamilyId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job clusters'));
        }
    };

    const copyJobLevels: JobArchitectureFetchers['copyJobLevels'] = async ({ jobLevels, jobRoleId }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/levels/${id}/copy-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job levels'));
        }
    };

    const copyJobRoles: JobArchitectureFetchers['copyJobRoles'] = async ({ jobClusterId, jobRoles }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/roles/${id}/copy-to/${jobClusterId}`,
                        requestOptions: {
                            searchParams: {
                                recursive: false,
                            },
                        },
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to copy job roles'));
        }
    };

    const fetchAssignableReviewers: JobArchitectureFetchers['fetchAssignableReviewers'] = async ({
        abortSignal,
        jobRoleOrJobLevel,
        searchTerm = '',
    }) => {
        const pathname: Pathname =
            jobRoleOrJobLevel.type === 'role'
                ? `/job-architectures/${jobRoleOrJobLevel.architectureId}/roles/${jobRoleOrJobLevel.id}/reviewers`
                : `/job-architectures/${jobRoleOrJobLevel.architectureId}/levels/${jobRoleOrJobLevel.id}/reviewers`;

        return validatingCobrainerApiFetcher.get(personsSchema, {
            pathname,
            requestOptions: {
                searchParams: {
                    searchTerm,
                },
                signal: abortSignal,
            },
        });
    };

    const fetchCareerTracks: JobArchitectureFetchers['fetchCareerTracks'] = async (jobArchitectureId) => {
        return validatingCobrainerApiFetcher.get(careerTrackWithTradeUnionsSchema.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/career-tracks`,
        });
    };

    const fetchCompetencies: JobArchitectureFetchers['fetchCompetencies'] = async () => {
        return validatingCobrainerApiFetcher.get(competenciesSchema, {
            pathname: '/skills/competencies',
        });
    };

    const fetchContributionTimeline: JobArchitectureFetchers['fetchContributionTimeline'] = async ({
        days = 30,
        jobArchitectureId,
        timezone,
    }) => {
        return validatingCobrainerApiFetcher.get(contributionTimelineSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/contributions/timeline`,
            requestOptions: {
                searchParams: {
                    days,
                    timezone,
                },
            },
        });
    };

    const fetchSkillDetails: JobArchitectureFetchers['fetchSkillDetails'] = async ({ language, skillId }) => {
        return validatingCobrainerApiFetcher.get(skillDetailsSchema, {
            pathname: `/skills/${skillId}/details`,
            requestOptions: {
                headers: {
                    'Accept-Language': language,
                },
            },
        });
    };

    const fetchSkillLimits: JobArchitectureFetchers['fetchSkillLimits'] = async ({ jobArchitectureId }) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureSkillLimitsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/skill-limits`,
        });
    };

    const fetchSkillLocales: JobArchitectureFetchers['fetchSkillLocales'] = async () => {
        return validatingCobrainerApiFetcher.get(z.string().array(), {
            pathname: '/skills/locales',
        });
    };

    const fetchJobLevelVersions: JobArchitectureFetchers['fetchJobLevelVersions'] = async ({
        jobArchitectureId,
        jobLevelId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions`,
        });
    };

    const fetchJobRoleVersions: JobArchitectureFetchers['fetchJobRoleVersions'] = async ({
        jobArchitectureId,
        jobRoleId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobProfileVersionsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions`,
        });
    };

    const fetchSkillsBySearchTerm: JobArchitectureFetchers['fetchSkillsBySearchTerm'] = async ({
        acceptLanguage,
        searchTerm,
    }) => {
        return validatingCobrainerApiFetcher.get(skillShortSchema.array(), {
            pathname: `/skills`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    limit: 10,
                    searchTerm,
                    type: ['hard', 'soft', 'language'].join(','),
                },
            },
        });
    };

    const fetchJobArchitectureSkills: JobArchitectureFetchers['fetchJobArchitectureSkills'] = async ({
        acceptLanguage,
        params,
    }) => {
        const { jobArchitectureIds, levelIds, paged, roleIds, searchTerm, skillTypes, sort, sources, statusFilter } =
            params;

        return validatingCobrainerApiFetcher.get(paginatedSkillCatalogSchema, {
            pathname: `/skill-catalog`,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams: {
                    countOnly: paged.countOnly,
                    jobArchitectureIds: jobArchitectureIds?.join(','),
                    levelIds: levelIds?.join(','),
                    page: paged.page,
                    roleIds: roleIds?.join(','),
                    searchTerm,
                    size: paged.size,
                    skillTypes: skillTypes?.join(','),
                    sort,
                    sources: sources?.join(','),
                    statusFilter: statusFilter?.join(','),
                },
                timeout: 50_000,
            },
        });
    };

    const fetchSkillCatalogOptions: JobArchitectureFetchers['fetchSkillCatalogOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(skillCatalogOptionsSchema, {
            pathname: `/skill-catalog/options`,
        });
    };

    const fetchSkillCatalogStatistics: JobArchitectureFetchers['fetchSkillCatalogStatistics'] = async ({
        jobArchitectureIds,
        levelIds,
        roleIds,
        searchTerm,
        skillTypes,
        sources,
        statusFilter,
    }) => {
        return validatingCobrainerApiFetcher.get(skillCatalogStatisticsSchema, {
            pathname: `/skill-catalog/statistics`,
            requestOptions: {
                searchParams: {
                    jobArchitectureIds: jobArchitectureIds?.join(','),
                    levelIds: levelIds?.join(','),
                    roleIds: roleIds?.join(','),
                    searchTerm,
                    skillTypes: skillTypes?.join(','),
                    sources: sources?.join(','),
                    statusFilter: statusFilter?.join(','),
                },
            },
        });
    };

    const createSkillCatalogEntry: JobArchitectureFetchers['createSkillCatalogEntry'] = async ({
        skillId,
        syncStatus,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/skill-catalog`,
            payload: {
                skillId,
                syncStatus,
            },
        });
    };

    const deleteSkillCatalogEntry: JobArchitectureFetchers['deleteSkillCatalogEntry'] = async ({ skillId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/skill-catalog/${skillId}`,
        });
    };

    const updateSkillCatalogEntry: JobArchitectureFetchers['updateSkillCatalogEntry'] = async ({
        skillId,
        syncStatus,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/skill-catalog/${skillId}`,
            payload: {
                syncStatus,
            },
        });
    };

    // --- Job Profile Template fetchers ---

    const fetchManagedJobProfileTemplates: JobArchitectureFetchers['fetchManagedJobProfileTemplates'] = async () => {
        return validatingCobrainerApiFetcher.get(managedJobProfileTemplateResponseArraySchema, {
            pathname: '/job-architecture/managed-job-profile-templates',
        });
    };

    const fetchCustomJobProfileTemplate: JobArchitectureFetchers['fetchCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(customJobProfileTemplateGetResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates`,
        });
    };

    const putCustomJobProfileTemplate: JobArchitectureFetchers['putCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
        templateId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.putJson(customJobProfileTemplateResponseSchema, {
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates/${templateId}`,
            payload,
            requestOptions: {
                timeout: 30_000,
            },
        });
    };

    const deleteCustomJobProfileTemplate: JobArchitectureFetchers['deleteCustomJobProfileTemplate'] = async ({
        jobArchitectureId,
        templateId,
    }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architecture/${jobArchitectureId}/job-profile-templates/${templateId}`,
        });
    };

    const postJobProfileTemplatePreview: JobArchitectureFetchers['postJobProfileTemplatePreview'] = async ({
        payload,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: '/job-architecture/job-profile-templates/preview',
            payload,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const fetchRelatedSkills: JobArchitectureFetchers['fetchRelatedSkills'] = async ({ acceptLanguage, skills }) => {
        return validatingCobrainerApiFetcher.postJson(skillShortSchema.array(), {
            pathname: `/skills/related`,
            payload: {
                hardSkillsLimit: 10,
                languageSkillsLimit: 1,
                skills,
                softSkillsLimit: 10,
            },
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
            },
        });
    };

    const fetchDebugInformation: JobArchitectureFetchers['fetchDebugInformation'] = async (item) => {
        const path = (item: JobCluster | JobFamily | JobLevel | JobRole): Pathname => {
            switch (item.type) {
                case 'job-family':
                    return `/job-architectures/${item.architectureId}/families/${item.id}/generationInfo`;
                case 'job-cluster':
                    return `/job-architectures/${item.architectureId}/clusters/${item.id}/generationInfo`;
                case 'job-role':
                    return `/job-architectures/${item.architectureId}/roles/${item.id}/generationInfo`;
                case 'job-level':
                    return `/job-architectures/${item.architectureId}/levels/${item.id}/generationInfo`;
            }
        };

        return validatingCobrainerApiFetcher.get(jobArchitectureDebugInfoSchema, {
            pathname: path(item),
        });
    };

    const fetchJobArchitecture: JobArchitectureFetchers['fetchJobArchitecture'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureSchema, {
            pathname: `/job-architectures/${jobArchitectureId}`,
            requestOptions: { signal },
        });
    };

    const createJobArchitecture: JobArchitectureFetchers['createJobArchitecture'] = async () => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSchema, {
            pathname: '/job-architectures',
            payload: {
                translations: [],
            },
        });
    };

    const fetchJobArchitectures: JobArchitectureFetchers['fetchJobArchitectures'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitecturesSchema, {
            pathname: '/job-architectures',
        });
    };

    const fetchJobArchitectureOptions: JobArchitectureFetchers['fetchJobArchitectureOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(jobArchitectureOptionsResponseSchema, {
            pathname: '/job-architectures/options',
        });
    };

    const fetchJobArchitectureMetadata: JobArchitectureFetchers['fetchJobArchitectureMetadata'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(nullableJobArchitectureMetadataSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
        });
    };

    const fetchCompanyInformationGenerationStatus: JobArchitectureFetchers['fetchCompanyInformationGenerationStatus'] =
        ({ jobArchitectureId, signal }) => {
            return validatingCobrainerApiFetcher.get(companyInformationGenerationResponseSchema, {
                pathname: `/job-architectures/${jobArchitectureId}/company-information/generate`,
                requestOptions: { signal },
            });
        };

    const submitCompanyInformationGeneration: JobArchitectureFetchers['submitCompanyInformationGeneration'] = async ({
        jobArchitectureId,
        payload,
        regenerate,
        signal,
    }) => {
        return validatingCobrainerApiFetcher.postJson(companyInformationGenerationResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/company-information/generate`,
            payload,
            requestOptions: {
                searchParams: { regenerate: String(regenerate) },
                signal,
            },
        });
    };

    const fetchJobArchitectureOnboardingState: JobArchitectureFetchers['fetchJobArchitectureOnboardingState'] = async (
        jobArchitectureId,
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureOnboardingStateSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/onboarding-state`,
        });
    };

    const patchJobArchitectureOnboardingState: JobArchitectureFetchers['patchJobArchitectureOnboardingState'] = async ({
        jobArchitectureId,
        onboardingState: updates,
    }) => {
        return validatingCobrainerApiFetcher.patchJson(jobArchitectureOnboardingStateSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/onboarding-state`,
            payload: updates,
        });
    };

    const fetchJobArchitectureInflight: JobArchitectureFetchers['fetchJobArchitectureInflight'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureInflightSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/inflight-tasks`,
            requestOptions: { signal },
        });
    };

    const fetchFamiliesWithClusters: JobArchitectureFetchers['fetchFamiliesWithClusters'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(familyWithClustersSchema.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families-with-clusters`,
            requestOptions: { signal },
        });
    };

    const fetchJobClusters: JobArchitectureFetchers['fetchJobClusters'] = async (
        jobArchitectureId,
        jobFamilyId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobClusterWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters`,
            requestOptions: { signal },
        });
    };

    const fetchJobFamilies: JobArchitectureFetchers['fetchJobFamilies'] = async (
        jobArchitectureId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobFamilyWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/families`,
            requestOptions: { signal },
        });
    };

    const fetchJobRoles: JobArchitectureFetchers['fetchJobRoles'] = async (
        jobArchitectureId,
        jobClusterId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles`,
            requestOptions: {
                searchParams: {
                    includeProfiles: false,
                },
                signal,
            },
        });
    };

    const fetchJobLevels: JobArchitectureFetchers['fetchJobLevels'] = async (
        jobArchitectureId,
        jobRoleId,
        { signal } = {},
    ) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels`,
            requestOptions: {
                searchParams: {
                    includeProfiles: false,
                },
                signal,
            },
        });
    };

    const deleteJobClusters: JobArchitectureFetchers['deleteJobClusters'] = async (jobClusters) => {
        const { architectureId } = jobClusters[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/clusters`,
            payload: jobClusters.map(({ id }) => id),
        });
    };

    const deleteJobFamilies: JobArchitectureFetchers['deleteJobFamilies'] = async (jobFamilies) => {
        const { architectureId } = jobFamilies[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/families`,
            payload: jobFamilies.map(({ id }) => id),
        });
    };

    const deleteJobLevels: JobArchitectureFetchers['deleteJobLevels'] = async (jobLevels) => {
        const { architectureId } = jobLevels[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/levels`,
            payload: jobLevels.map(({ id }) => id),
        });
    };

    const deleteJobRoles: JobArchitectureFetchers['deleteJobRoles'] = async (jobRoles) => {
        const { architectureId } = jobRoles[0];

        return cobrainerApiFetcher.deleteJson({
            pathname: `/job-architectures/${architectureId}/roles`,
            payload: jobRoles.map(({ id }) => id),
        });
    };

    const exportJobArchitecture: JobArchitectureFetchers['exportJobArchitecture'] = async ({
        jobArchitectureId,
        languageCode,
    }) => {
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/export`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
                timeout: 60_000,
            },
        });
    };

    const requestChangelogExport: JobArchitectureFetchers['requestChangelogExport'] = async ({
        jobArchitectureId,
        dateFrom,
        dateTo,
        familyOrClusterId,
        recipientEmail,
    }) => {
        return validatingCobrainerApiFetcher.postJson(changelogExportResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/changelog/export`,
            payload: {
                ...(dateFrom !== undefined && { dateFrom }),
                ...(dateTo !== undefined && { dateTo }),
                ...(familyOrClusterId !== undefined && { familyOrClusterId }),
                ...(recipientEmail !== undefined && recipientEmail !== '' && { recipientEmail }),
            },
        });
    };

    const exportJobLevel: JobArchitectureFetchers['exportJobLevel'] = async ({
        jobArchitectureId,
        jobLevelId,
        languageCode,
        version,
    }) => {
        const versionQuery = is.number(version) ? `?version=${version}` : '';
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/export${versionQuery}`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const exportJobRole: JobArchitectureFetchers['exportJobRole'] = async ({
        jobArchitectureId,
        jobRoleId,
        languageCode,
        version,
    }) => {
        const versionQuery = is.number(version) ? `?version=${version}` : '';
        const pathname: Pathname = `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/export${versionQuery}`;
        const origin = window.location.origin;
        const headers = {
            'Accept-Language': languageCode,
            Origin: origin,
        };

        return validatingCobrainerApiFetcher.post(downloadLinkResponseSchema, {
            pathname,
            requestOptions: {
                headers,
            },
        });
    };

    const patchJobArchitecture: JobArchitectureFetchers['patchJobArchitecture'] = async ({
        jobArchitecture,
        languageCode,
    }) => {
        const { id, title } = jobArchitecture;

        return validatingCobrainerApiFetcher.patchJson(jobArchitectureSchema, {
            pathname: `/job-architectures/${id}`,
            payload: {
                translations: [
                    {
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobArchitectureMetadata: JobArchitectureFetchers['patchJobArchitectureMetadata'] = async ({
        jobArchitectureId,
        metadata,
        allowJsaRename,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/metadata`,
            payload: metadata,
            requestOptions: allowJsaRename === true ? { searchParams: { allowJsaRename: 'true' } } : undefined,
        });
    };

    const patchJobFamily: JobArchitectureFetchers['patchJobFamily'] = async ({ jobFamily }) => {
        const { id, architectureId } = jobFamily;

        return validatingCobrainerApiFetcher.patchJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/families/${id}`,
            payload: {
                translations: jobFamily.translations,
            },
        });
    };

    const patchJobCluster: JobArchitectureFetchers['patchJobCluster'] = async ({ jobCluster }) => {
        const { id, architectureId, translations } = jobCluster;

        return validatingCobrainerApiFetcher.patchJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/clusters/${id}`,
            payload: {
                translations,
            },
        });
    };

    const patchJobLevel: JobArchitectureFetchers['patchJobLevel'] = async ({ jobLevel, languageCode }) => {
        const { id, architectureId, description, title } = jobLevel;

        return validatingCobrainerApiFetcher.patchJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/levels/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const patchJobRole: JobArchitectureFetchers['patchJobRole'] = async ({ jobRole, languageCode }) => {
        const { id, architectureId, description, title } = jobRole;

        return validatingCobrainerApiFetcher.patchJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${architectureId}/roles/${id}`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const putJobCluster: JobArchitectureFetchers['putJobCluster'] = async ({
        jobArchitectureId,
        jobCluster,
        jobFamilyId,
    }) => {
        return validatingCobrainerApiFetcher.putJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/${jobCluster.id}`,
            payload: { translations: jobCluster.translations },
        });
    };

    const putJobFamily: JobArchitectureFetchers['putJobFamily'] = async ({ jobArchitectureId, jobFamily }) => {
        return validatingCobrainerApiFetcher.putJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamily.id}`,
            payload: { translations: jobFamily.translations },
        });
    };

    const bulkApplyOnboardingDraft: JobArchitectureFetchers['bulkApplyOnboardingDraft'] = async ({
        draft,
        jobArchitectureId,
        languageCode,
    }) => {
        try {
            await applyOnboardingDraft(
                { draft, jobArchitectureId, languageCode },
                {
                    deleteJobClusters,
                    deleteJobFamilies,
                    patchJobCluster,
                    patchJobFamily,
                    putJobCluster,
                    putJobFamily,
                },
            );

            return Result.ok(undefined);
        } catch (error) {
            return Result.err(error instanceof Error ? error : new Error(String(error)));
        }
    };

    const persistJobCluster: JobArchitectureFetchers['persistJobCluster'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobClusterWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families/${parentId}/clusters?generateDescription=true`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobFamily: JobArchitectureFetchers['persistJobFamily'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobFamilyWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/families?generateDescription=true`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobLevel: JobArchitectureFetchers['persistJobLevel'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${parentId}/levels`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const persistJobRole: JobArchitectureFetchers['persistJobRole'] = async ({
        jobArchitectureId,
        languageCode,
        payload,
    }) => {
        const { description, parentId, title } = payload;

        return validatingCobrainerApiFetcher.postJson(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${parentId}/roles`,
            payload: {
                translations: [
                    {
                        description,
                        language: languageCode,
                        title,
                    },
                ],
            },
        });
    };

    const editJobLevelVersionNote: JobArchitectureFetchers['editJobLevelVersionNote'] = async ({
        comment,
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}`,
            payload: { comment },
        });
    };

    const editJobRoleVersionNote: JobArchitectureFetchers['editJobRoleVersionNote'] = async ({
        comment,
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.patchJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}`,
            payload: { comment },
        });
    };

    const publishJobLevelVersion: JobArchitectureFetchers['publishJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/publish`,
        });
    };

    const publishJobRoleVersion: JobArchitectureFetchers['publishJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/publish`,
        });
    };

    const reviewJobLevelVersion: JobArchitectureFetchers['reviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/review`,
        });
    };

    const reviewJobRoleVersion: JobArchitectureFetchers['reviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/review`,
        });
    };

    const unreviewJobLevelVersion: JobArchitectureFetchers['unreviewJobLevelVersion'] = async ({
        jobArchitectureId,
        jobLevelId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/versions/${version}/unreview`,
        });
    };

    const unreviewJobRoleVersion: JobArchitectureFetchers['unreviewJobRoleVersion'] = async ({
        jobArchitectureId,
        jobRoleId,
        version,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/versions/${version}/unreview`,
        });
    };

    const readJobLevel: JobArchitectureFetchers['readJobLevel'] = async ({ jobArchitectureId, jobLevelId, signal }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}`,
            requestOptions: { signal },
        });
    };

    const readJobRole: JobArchitectureFetchers['readJobRole'] = async ({ jobArchitectureId, jobRoleId, signal }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}`,
            requestOptions: { signal },
        });
    };

    const readJobLevelDetails: JobArchitectureFetchers['readJobLevelDetails'] = async ({
        includeComments,
        jobArchitectureId,
        jobLevelId,
        signal,
        version,
    }) => {
        const queryParams = new URLSearchParams();
        if (!is.undefined(version)) {
            queryParams.set('version', version);
        }
        if (includeComments === true) {
            queryParams.set('includeComments', 'true');
        }

        return validatingCobrainerApiFetcher.get(jobLevelDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}?${queryParams.toString()}`,
            requestOptions: { signal },
        });
    };

    const readJobRoleDetails: JobArchitectureFetchers['readJobRoleDetails'] = async ({
        includeComments,
        jobArchitectureId,
        jobRoleId,
        signal,
        version,
    }) => {
        const queryParams = new URLSearchParams();
        if (!is.undefined(version)) {
            queryParams.set('version', version);
        }
        if (includeComments === true) {
            queryParams.set('includeComments', 'true');
        }

        return await validatingCobrainerApiFetcher.get(jobRoleDetailsSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}?${queryParams.toString()}`,
            requestOptions: { signal },
        });
    };

    const redetectSkills: JobArchitectureFetchers['redetectSkills'] = async ({
        additionalPrompt,
        commentsContext,
        detectionType,
        jobProfile,
        jobProfileId,
    }) => {
        const requestPayload = {
            additionalPrompt: {
                type: detectionType,
                userInput: additionalPrompt.unwrapOr(undefined),
            },
            ...(commentsContext ? { commentsContext } : {}),
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(redetectSkillsWithPromptSchema, {
            pathname: `/job-profiles/${jobProfileId}/detect-skills`,
            payload: requestPayload,
            requestOptions: {
                timeout: 30_000,
            },
        });
    };

    const updateJobProfile: JobArchitectureFetchers['updateJobProfile'] = async ({
        acceptLanguage,
        jobProfile,
        versionComment,
        withAutoTranslation,
    }) => {
        const searchParams = withAutoTranslation
            ? { asyncTranslation: true, shouldTranslate: true }
            : { shouldTranslate: false };

        return validatingCobrainerApiFetcher.patchJson(jobProfileLegacySchema, {
            pathname: `/job-profiles/${jobProfile.id}`,
            payload: { ...jobProfile, versionComment },
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                searchParams,
            },
        });
    };

    const generateJobArchitectureWithAI: JobArchitectureFetchers['generateJobArchitectureWithAI'] = async ({
        awaitKnowledgeBaseIngestion,
        jobArchitectureId,
        type,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/generate`,
            requestOptions: {
                searchParams: {
                    async: true,
                    awaitKnowledgeBaseIngestion,
                    type,
                },
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobFamilyWithAI: JobArchitectureFetchers['generateJobFamilyWithAI'] = async (jobArchitectureId) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobClustersWithAI: JobArchitectureFetchers['generateJobClustersWithAI'] = async ({
        jobArchitectureId,
        jobFamilyId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/families/${jobFamilyId}/clusters/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const getJobArchitectureItemPath: JobArchitectureFetchers['getJobArchitectureItemPath'] = async ({
        itemId,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitecturePathSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/items/${itemId}/path`,
        });
    };

    const getUsers: JobArchitectureFetchers['getUsers'] = async ({ paged, role, sort }) => {
        return validatingCobrainerApiFetcher.get(userPaginationSchema, {
            pathname: '/users',
            requestOptions: {
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                    ...(!is.nullOrUndefined(role) ? { role } : {}),
                    ...(!is.nullOrUndefined(sort) ? { sort } : {}),
                },
            },
        });
    };

    const generateJobRolesWithAI: JobArchitectureFetchers['generateJobRolesWithAI'] = async ({
        jobArchitectureId,
        jobClusterId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/clusters/${jobClusterId}/roles/generate?async=true`,
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const generateJobLevelsWithAI: JobArchitectureFetchers['generateJobLevelsWithAI'] = async ({
        autoSelectTracks,
        careerTracks,
        createRoleCopy,
        jobArchitectureId,
        jobRoleId,
        respectSkillsFromRole,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/levels/generate?async=true`,
            payload: {
                autoSelectTracks,
                careerTracks,
                createRoleCopy,
                respectSkillsFromRole,
            },
            requestOptions: {
                timeout: generateWithAiRequestTimeout,
            },
        });
    };

    const moveJobClusters: JobArchitectureFetchers['moveJobClusters'] = async ({ jobClusters, jobFamilyId }) => {
        try {
            const results = await Promise.all(
                jobClusters.map(({ id, architectureId }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${architectureId}/clusters/${id}/move-to/${jobFamilyId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job clusters'));
        }
    };

    const moveJobLevels: JobArchitectureFetchers['moveJobLevels'] = async ({
        jobArchitectureId,
        jobLevels,
        jobRoleId,
    }) => {
        try {
            const results = await Promise.all(
                jobLevels.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/levels/${id}/move-to/${jobRoleId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job levels'));
        }
    };

    const moveJobRoles: JobArchitectureFetchers['moveJobRoles'] = async ({
        jobArchitectureId,
        jobClusterId,
        jobRoles,
    }) => {
        try {
            const results = await Promise.all(
                jobRoles.map(({ id }) => {
                    return cobrainerApiFetcher.post({
                        pathname: `/job-architectures/${jobArchitectureId}/roles/${id}/move-to/${jobClusterId}`,
                    });
                }),
            );

            return Result.ok(results);
        } catch {
            return Result.err(new Error('failed to move job roles'));
        }
    };

    const regenerateJobProfileDescription: JobArchitectureFetchers['regenerateJobProfileDescription'] = ({
        acceptLanguage,
        additionalPrompt,
        commentsContext,
        jobProfile,
    }) => {
        const requestPayload = {
            additionalPrompt: additionalPrompt.unwrapOr(undefined),
            ...(commentsContext ? { commentsContext } : {}),
            jobProfile,
        };

        return validatingCobrainerApiFetcher.postJson(jobProfileRegenerationResponseSchema, {
            pathname: `/job-profiles/${jobProfile.id}/regenerate`,
            payload: requestPayload,
            requestOptions: {
                headers: {
                    'Accept-Language': acceptLanguage,
                },
                timeout: 60_000,
            },
        });
    };

    const deleteFamiliesRegenerationResults: JobArchitectureFetchers['deleteFamiliesRegenerationResults'] = async ({
        jobArchitectureId,
        regenerationId,
    }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results/${regenerationId}`,
        });
    };

    const fetchFamiliesRegenerationResults: JobArchitectureFetchers['fetchFamiliesRegenerationResults'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(familiesRegenerationResultSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results`,
        });
    };

    const fetchRegenerationResult: JobArchitectureFetchers['fetchRegenerationResult'] = async ({
        jobArchitectureId,
        regenerationId,
    }) => {
        return validatingCobrainerApiFetcher.get(jsaRegenerationResultSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regeneration-results/${regenerationId}`,
        });
    };

    const regenerateOnboarding: JobArchitectureFetchers['regenerateOnboarding'] = async ({
        jobArchitectureId,
        jsaDraft,
        prompt,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jsaStructureRegenerationResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/families/regenerate`,
            payload: { currentPreview: jsaDraft, userMessage: prompt },
            requestOptions: {
                timeout: 60_000,
            },
        });
    };

    const fetchJobArchitectureFilters: JobArchitectureFetchers['fetchJobArchitectureFilters'] = async ({
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobArchitectureFiltersResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter`,
        });
    };

    const fetchJobArchitectureHistory: JobArchitectureFetchers['fetchJobArchitectureHistory'] = async ({
        jobArchitectureId,
        params,
    }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedJobArchitectureHistorySchema, {
            pathname: `/job-architectures/${jobArchitectureId}/history`,
            requestOptions: {
                searchParams: {
                    page: paged.page,
                    size: paged.size,
                    ...(paged.countOnly === true ? { countOnly: paged.countOnly } : {}),
                },
            },
        });
    };

    type SearchFilterPayload =
        | { field: string; ignore_case?: boolean; op: string; value: string }
        | { filters: Array<{ field: string; ignore_case?: boolean; op: string; value: string }>; op: string };

    const buildSearchFilter = (args: {
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }): SearchFilterPayload => {
        const { reviewerId, searchTerm, type } = args;

        const filters: Array<{ field: string; ignore_case?: boolean; op: string; value: string }> = [];

        if (is.nonEmptyString(type)) {
            filters.push({
                field: 'level',
                op: 'eq',
                value: type,
            });
        }

        if (is.nonEmptyString(reviewerId)) {
            filters.push({
                field: 'reviewerId',
                ignore_case: true,
                op: 'eq',
                value: reviewerId,
            });
        }

        if (is.nonEmptyString(searchTerm)) {
            filters.push({
                field: 'title',
                ignore_case: true,
                op: 'contains',
                value: searchTerm,
            });
        }

        if (filters.length === 1) {
            return filters[0]!;
        }

        return {
            filters,
            op: 'and',
        };
    };

    const buildSearchPayload = (args: {
        readonly page: number;
        readonly reviewerId?: string;
        readonly searchTerm?: string;
        readonly size: number;
        readonly type?: 'cluster' | 'family' | 'level' | 'role';
    }) => ({
        filter: buildSearchFilter({ reviewerId: args.reviewerId, searchTerm: args.searchTerm, type: args.type }),
        paged: {
            page: args.page,
            size: args.size,
        },
        sorted: {
            fields: [
                {
                    direction: 'asc',
                    field: 'levelNumber',
                },
            ],
        },
    });

    const searchJobArchitecture: JobArchitectureFetchers['searchJobArchitecture'] = async ({
        jobArchitectureId,
        page = 0,
        reviewerId,
        searchTerm,
        size = 100,
        type,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSearchResponseSchema, {
            pathname: `/job-architectures/${jobArchitectureId}/filter`,
            payload: buildSearchPayload({ page, reviewerId, searchTerm, size, type }),
        });
    };

    const searchJobArchitectures: JobArchitectureFetchers['searchJobArchitectures'] = async ({
        page = 0,
        reviewerId,
        searchTerm,
        size = 100,
        type,
    }) => {
        return validatingCobrainerApiFetcher.postJson(jobArchitectureSearchResponseSchema, {
            pathname: '/job-architectures/filter',
            payload: buildSearchPayload({ page, reviewerId, searchTerm, size, type }),
        });
    };

    const fetchLevelingFrameworks: JobArchitectureFetchers['fetchLevelingFrameworks'] = async ({ params }) => {
        const { paged } = params;

        return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            requestOptions: {
                searchParams: {
                    countOnly: paged.countOnly,
                    page: paged.page,
                    size: paged.size,
                },
            },
        });
    };

    const createLevelingFramework: JobArchitectureFetchers['createLevelingFramework'] = async ({ payload }) => {
        return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
            pathname: '/leveling-frameworks',
            payload,
        });
    };

    const fetchLevelingFramework: JobArchitectureFetchers['fetchLevelingFramework'] = async ({ frameworkId }) => {
        return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const updateLevelingFramework: JobArchitectureFetchers['updateLevelingFramework'] = async ({
        frameworkId,
        payload,
    }) => {
        return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
            pathname: `/leveling-frameworks/${frameworkId}`,
            payload,
        });
    };

    const deleteLevelingFramework: JobArchitectureFetchers['deleteLevelingFramework'] = async ({ frameworkId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/leveling-frameworks/${frameworkId}`,
        });
    };

    const fetchJobArchitectureLevelingFrameworks: JobArchitectureFetchers['fetchJobArchitectureLevelingFrameworks'] =
        async ({ jobArchitectureId, params }) => {
            const { paged } = params;

            return validatingCobrainerApiFetcher.get(paginatedLevelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                requestOptions: {
                    searchParams: {
                        countOnly: paged.countOnly,
                        page: paged.page,
                        size: paged.size,
                    },
                },
            });
        };

    const createJobArchitectureLevelingFramework: JobArchitectureFetchers['createJobArchitectureLevelingFramework'] =
        async ({ jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.postJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks`,
                payload,
            });
        };

    const fetchJobArchitectureLevelingFramework: JobArchitectureFetchers['fetchJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return validatingCobrainerApiFetcher.get(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const updateJobArchitectureLevelingFramework: JobArchitectureFetchers['updateJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId, payload }) => {
            return validatingCobrainerApiFetcher.putJson(levelingFrameworkSchema, {
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
                payload,
            });
        };

    const deleteJobArchitectureLevelingFramework: JobArchitectureFetchers['deleteJobArchitectureLevelingFramework'] =
        async ({ frameworkId, jobArchitectureId }) => {
            return cobrainerApiFetcher.delete({
                pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/${frameworkId}`,
            });
        };

    const deleteJobArchitecture: JobArchitectureFetchers['deleteJobArchitecture'] = async ({ jobArchitectureId }) => {
        return cobrainerApiFetcher.delete({
            pathname: `/job-architectures/${jobArchitectureId}`,
        });
    };

    const assignDefaultLevelingFramework: JobArchitectureFetchers['assignDefaultLevelingFramework'] = async ({
        jobArchitectureId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architecture/${jobArchitectureId}/leveling-frameworks/assign-default`,
        });
    };

    const archiveJobLevel: JobArchitectureFetchers['archiveJobLevel'] = async ({ jobArchitectureId, jobLevelId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/archive`,
        });
    };

    const archiveJobLevels: JobArchitectureFetchers['archiveJobLevels'] = async ({
        jobArchitectureId,
        jobLevelIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/archive`,
            payload: jobLevelIds,
        });
    };

    const archiveJobRole: JobArchitectureFetchers['archiveJobRole'] = async ({ jobArchitectureId, jobRoleId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/archive`,
        });
    };

    const archiveJobRoles: JobArchitectureFetchers['archiveJobRoles'] = async ({ jobArchitectureId, jobRoleIds }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/archive`,
            payload: jobRoleIds,
        });
    };

    const fetchArchivedJobLevels: JobArchitectureFetchers['fetchArchivedJobLevels'] = async ({
        includeProfiles = true,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobLevelWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/levels/archived`,
            requestOptions: {
                searchParams: {
                    includeProfiles,
                },
            },
        });
    };

    const fetchArchivedJobRoles: JobArchitectureFetchers['fetchArchivedJobRoles'] = async ({
        includeProfiles = true,
        jobArchitectureId,
    }) => {
        return validatingCobrainerApiFetcher.get(jobRoleWithTypeSchemaTransformed.array(), {
            pathname: `/job-architectures/${jobArchitectureId}/roles/archived`,
            requestOptions: {
                searchParams: {
                    includeProfiles,
                },
            },
        });
    };

    const unarchiveJobLevel: JobArchitectureFetchers['unarchiveJobLevel'] = async ({
        jobArchitectureId,
        jobLevelId,
    }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${jobLevelId}/unarchive`,
        });
    };

    const unarchiveJobLevels: JobArchitectureFetchers['unarchiveJobLevels'] = async ({
        jobArchitectureId,
        jobLevelIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/unarchive`,
            payload: jobLevelIds,
        });
    };

    const inviteAndAssignToLevel: JobArchitectureFetchers['inviteAndAssignToLevel'] = async ({
        jobArchitectureId,
        levelId,
        request,
    }) => {
        const { inviteLanguage, ...payload } = request;

        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/levels/${levelId}/invite-and-assign`,
            payload,
            requestOptions: {
                searchParams: new URLSearchParams(inviteLanguage !== undefined ? { inviteLanguage } : {}),
            },
        });
    };

    const inviteAndAssignToRole: JobArchitectureFetchers['inviteAndAssignToRole'] = async ({
        jobArchitectureId,
        request,
        roleId,
    }) => {
        const { inviteLanguage, ...payload } = request;

        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${roleId}/invite-and-assign`,
            payload,
            requestOptions: {
                searchParams: new URLSearchParams(inviteLanguage !== undefined ? { inviteLanguage } : {}),
            },
        });
    };

    const unarchiveJobRole: JobArchitectureFetchers['unarchiveJobRole'] = async ({ jobArchitectureId, jobRoleId }) => {
        return cobrainerApiFetcher.post({
            pathname: `/job-architectures/${jobArchitectureId}/roles/${jobRoleId}/unarchive`,
        });
    };

    const unarchiveJobRoles: JobArchitectureFetchers['unarchiveJobRoles'] = async ({
        jobArchitectureId,
        jobRoleIds,
    }) => {
        return cobrainerApiFetcher.postJson({
            pathname: `/job-architectures/${jobArchitectureId}/roles/unarchive`,
            payload: jobRoleIds,
        });
    };

    return {
        addCareerTrack,
        archiveJobLevel,
        archiveJobLevels,
        archiveJobRole,
        archiveJobRoles,
        assignDefaultLevelingFramework,
        assignReviewer,
        bulkApplyOnboardingDraft,
        copyJobClusters,
        copyJobLevels,
        copyJobRoles,
        createJobArchitecture,
        createJobArchitectureLevelingFramework,
        createLevelingFramework,
        createSkillCatalogEntry,
        deleteFamiliesRegenerationResults,
        deleteJobArchitecture,
        deleteJobArchitectureLevelingFramework,
        deleteJobClusters,
        deleteJobFamilies,
        deleteJobLevels,
        deleteJobRoles,
        deleteLevelingFramework,
        deleteSkillCatalogEntry,
        editJobLevelVersionNote,
        editJobRoleVersionNote,
        exportJobArchitecture,
        exportJobLevel,
        exportJobRole,
        requestChangelogExport,
        fetchArchivedJobLevels,
        fetchArchivedJobRoles,
        fetchAssignableReviewers,
        fetchCareerTracks,
        fetchCompanyInformationGenerationStatus,
        fetchCompetencies,
        fetchContributionTimeline,
        fetchDebugInformation,
        fetchFamiliesWithClusters,
        fetchJobArchitecture,
        fetchJobArchitectureFilters,
        fetchJobArchitectureHistory,
        fetchJobArchitectureInflight,
        fetchJobArchitectureLevelingFramework,
        fetchJobArchitectureLevelingFrameworks,
        fetchJobArchitectureMetadata,
        fetchJobArchitectureOnboardingState,
        fetchJobArchitectureOptions,
        fetchJobArchitectures,
        fetchJobArchitectureSkills,
        fetchJobClusters,
        fetchJobFamilies,
        fetchJobLevels,
        fetchJobLevelVersions,
        fetchJobRoles,
        fetchJobRoleVersions,
        fetchFamiliesRegenerationResults,
        fetchRegenerationResult,
        fetchLevelingFramework,
        fetchLevelingFrameworks,
        fetchRelatedSkills,
        fetchSkillCatalogOptions,
        fetchSkillCatalogStatistics,
        fetchSkillDetails,
        fetchSkillLimits,
        fetchSkillLocales,
        fetchSkillsBySearchTerm,
        generateJobArchitectureWithAI,
        generateJobClustersWithAI,
        generateJobFamilyWithAI,
        generateJobLevelsWithAI,
        generateJobRolesWithAI,
        getJobArchitectureItemPath,
        getUsers,
        inviteAndAssignToLevel,
        inviteAndAssignToRole,
        moveJobClusters,
        moveJobLevels,
        moveJobRoles,
        patchJobArchitecture,
        patchJobArchitectureMetadata,
        patchJobArchitectureOnboardingState,
        patchJobCluster,
        patchJobFamily,
        patchJobLevel,
        patchJobRole,
        persistJobCluster,
        persistJobFamily,
        persistJobLevel,
        persistJobRole,
        publishJobLevelVersion,
        publishJobRoleVersion,
        putJobCluster,
        putJobFamily,
        readJobLevel,
        readJobLevelDetails,
        readJobRole,
        readJobRoleDetails,
        redetectSkills,
        regenerateJobProfileDescription,
        regenerateOnboarding,
        reviewJobLevelVersion,
        reviewJobRoleVersion,
        searchJobArchitecture,
        searchJobArchitectures,
        submitCompanyInformationGeneration,
        unarchiveJobLevel,
        unarchiveJobLevels,
        unarchiveJobRole,
        unarchiveJobRoles,
        unassignReviewer,
        unreviewJobLevelVersion,
        unreviewJobRoleVersion,
        updateJobArchitectureLevelingFramework,
        updateJobProfile,
        updateLevelingFramework,
        updateSkillCatalogEntry,
        fetchManagedJobProfileTemplates,
        fetchCustomJobProfileTemplate,
        putCustomJobProfileTemplate,
        deleteCustomJobProfileTemplate,
        postJobProfileTemplatePreview,
    };
};
